import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import type {
  AppData,
  BudgetAssumption,
  Client,
  CompanyProfile,
  MonthlyCost,
  ToolItem,
  WorkActivity,
  WorkActivityInput,
} from "../types";

const emptyData: AppData = {
  company: null,
  team: [],
  clients: [],
  activities: [],
  monthlyCosts: [],
  tools: [],
  assumptions: [],
  sources: [],
};

function cleanPayload<T extends object>(value: T): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(value).filter(
      ([key]) =>
        ![
          "created_at",
          "updated_at",
          "created_by",
          "calculatedQuantity",
          "calculatedUnitCost",
          "monthlyCost",
        ].includes(key),
    ),
  );
}

export function useAppData(enabled: boolean) {
  const [data, setData] = useState<AppData>(emptyData);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    if (!enabled || !supabase) return;
    setLoading(true);
    setError("");
    try {
      const [company, team, clients, activities, monthlyCosts, tools, assumptions, sources] =
        await Promise.all([
          supabase.from("company_profile").select("*").limit(1).maybeSingle(),
          supabase
            .from("team_members")
            .select("*")
            .eq("active", true)
            .order("display_name"),
          supabase.from("clients").select("*").order("sort_order"),
          supabase
            .from("work_activities")
            .select("*")
            .order("work_date", { ascending: false })
            .order("created_at", { ascending: false }),
          supabase.from("monthly_costs").select("*").order("sort_order"),
          supabase.from("tools_equipment").select("*").order("sort_order"),
          supabase.from("budget_assumptions").select("*").order("sort_order"),
          supabase.from("source_log").select("*").order("sort_order"),
        ]);

      const results = [
        company,
        team,
        clients,
        activities,
        monthlyCosts,
        tools,
        assumptions,
        sources,
      ];
      const failure = results.find((result) => result.error);
      if (failure?.error) throw failure.error;

      setData({
        company: (company.data as AppData["company"]) || null,
        team: (team.data as AppData["team"]) || [],
        clients: (clients.data as AppData["clients"]) || [],
        activities: (activities.data as AppData["activities"]) || [],
        monthlyCosts: (monthlyCosts.data as AppData["monthlyCosts"]) || [],
        tools: (tools.data as AppData["tools"]) || [],
        assumptions: (assumptions.data as AppData["assumptions"]) || [],
        sources: (sources.data as AppData["sources"]) || [],
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load data.");
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const upsert = useCallback(
    async (table: string, payload: Record<string, unknown>) => {
      if (!supabase) throw new Error("Supabase is not configured.");
      const { error: saveError } = await supabase.from(table).upsert(payload);
      if (saveError) throw saveError;
      await refresh();
    },
    [refresh],
  );

  const remove = useCallback(
    async (table: string, idColumn: string, id: string) => {
      if (!supabase) throw new Error("Supabase is not configured.");
      const { error: removeError } = await supabase
        .from(table)
        .delete()
        .eq(idColumn, id);
      if (removeError) throw removeError;
      await refresh();
    },
    [refresh],
  );

  return {
    data,
    loading,
    error,
    refresh,
    async saveActivity(activity: WorkActivity | WorkActivityInput) {
      await upsert("work_activities", cleanPayload(activity));
    },
    async deleteActivity(id: string) {
      await remove("work_activities", "id", id);
    },
    async saveClient(client: Client) {
      await upsert("clients", cleanPayload(client));
    },
    async deleteClient(id: string) {
      await remove("clients", "id", id);
    },
    async saveMonthlyCost(cost: MonthlyCost) {
      await upsert("monthly_costs", cleanPayload(cost));
    },
    async saveTool(tool: ToolItem) {
      await upsert("tools_equipment", cleanPayload(tool));
    },
    async saveAssumption(assumption: BudgetAssumption) {
      await upsert("budget_assumptions", cleanPayload(assumption));
    },
    async saveCompany(company: CompanyProfile) {
      await upsert("company_profile", cleanPayload(company));
    },
  };
}
